import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    
    console.log('[API] Request:', {
      url: config.url,
      hasToken: !!token,
      tokenLength: token?.length
    });
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('[API] ⚠️ No token available for request to:', config.url);
    }
    
    return config;
  },
  (error) => {
    console.error('[API] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - simple 401 handling
// Descope SDK handles token refresh automatically
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If 401, session is expired and Descope SDK couldn't refresh
    // This means user needs to login again
    if (error.response?.status === 401) {
      console.log('[API] ❌ Session expired, redirecting to login...');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      
      // Redirect to login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Dynamic API service class
class ApiService {
  constructor() {
    this.api = api; // Expose axios instance for direct access
  }

  // Auth endpoints
  async validateToken() {
    return api.post('/auth/validate-token');
  }

  async getMe() {
    return api.get('/auth/me');
  }

  async getRoles() {
    return api.get('/auth/roles');
  }

  async getPermissions() {
    return api.get('/auth/permissions');
  }

  // Facilities endpoints
  async getFacilities() {
    return api.get('/asphalt/facilities');
  }

  async getFacilityDefaults(facilityName) {
    return api.get(`/asphalt/facilities/${encodeURIComponent(facilityName)}/defaults`);
  }

  async getMyFacilities() {
    return api.get('/asphalt/my-facilities');
  }

  // EXTENDED Scenarios endpoints - Now handles all 6 stages
  async getScenarios() {
    return api.get('/asphalt/scenarios');
  }

  async getScenario(id) {
    return api.get(`/asphalt/scenarios/${id}`);
  }

  // EXTENDED createScenario - Now supports all 6 stages
  async createScenario(scenarioData) {
    // Ensure all required stages are present with at least empty objects
    const extendedScenarioData = {
      ...scenarioData,
      quarry: scenarioData.quarry || {},
      transport: scenarioData.transport || {},
      asphalt_manufacturing: scenarioData.asphalt_manufacturing || {},
      // NEW STAGES - Ensure they exist
      transport_from_plant: scenarioData.transport_from_plant || {},
      maintenance: scenarioData.maintenance || {},
      end_of_life: scenarioData.end_of_life || {}
    };
    
    console.log('Creating extended scenario with data:', extendedScenarioData);
    return api.post('/asphalt/scenarios', extendedScenarioData);
  }

  async exportExtendedLCAReport(scenarioId) {
    try {
      const response = await api.get(
        `/asphalt/scenarios/${scenarioId}/export-lca-report`,
        {
          responseType: 'blob',
          timeout: 120000
        }
      );

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Extended_LCA_Report.docx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Report exported successfully' };
    } catch (error) {
      console.error('Error exporting LCA report:', error);
      throw error;
    }
  }

  async downloadEPD(scenarioId, config = {}) {
    return api.get(
      `/asphalt/scenarios/${scenarioId}/export-epd`,
      {
        responseType: 'blob',
        timeout: 120000,
        ...config
      }
    );
  }

  async generateEPD(scenarioId, payload = {}) {
    return api.post(`/asphalt/scenarios/${scenarioId}/generate-epd`, payload);
  }

  // EXTENDED updateScenario - Now supports all 6 stages
  async updateScenario(id, scenarioData) {
    // Ensure all required stages are present with at least empty objects
    const extendedScenarioData = {
      ...scenarioData,
      quarry: scenarioData.quarry || {},
      transport: scenarioData.transport || {},
      asphalt_manufacturing: scenarioData.asphalt_manufacturing || {},
      // NEW STAGES - Ensure they exist
      transport_from_plant: scenarioData.transport_from_plant || {},
      maintenance: scenarioData.maintenance || {},
      end_of_life: scenarioData.end_of_life || {}
    };
    
    console.log('Updating extended scenario with data:', extendedScenarioData);
    return api.put(`/asphalt/scenarios/${id}`, extendedScenarioData);
  }

  async deleteScenario(id) {
    return api.delete(`/asphalt/scenarios/${id}`);
  }

  async recalculateScenario(id) {
    return api.post(`/asphalt/scenarios/${id}/recalculate`);
  }

  async getFacilityScenarios(facilityId) {
    return api.get(`/asphalt/facilities/${facilityId}/scenarios`);
  }

  async getUserScenarios(userId) {
    return api.get(`/asphalt/scenarios/by-user/${userId}`);
  }

  // NEW EXTENDED ENDPOINT - Get detailed emissions breakdown for all 6 stages
  async getEmissionsBreakdown(scenarioId) {
    return api.get(`/asphalt/scenarios/${scenarioId}/emissions-breakdown`);
  }

  // NEW ENDPOINT - Get EPD status for a scenario
  async getEPDStatus(scenarioId) {
    return api.get(`/asphalt/scenarios/${scenarioId}/epd-status`);
  }

  // Emission factors endpoints
  async getEmissionFactors() {
    return api.get('/emission-factors/');
  }

  async updateEmissionFactors(factorsData) {
    return api.put('/emission-factors/', factorsData);
  }

  // Admin endpoints (Executive Director only)
  async getAllUsers() {
    return api.get('/admin/users');
  }

  async getUser(userId) {
    return api.get(`/admin/users/${userId}`);
  }

  async createUser(userData) {
    return api.post('/admin/users', userData);
  }

  async updateUser(userId, userData) {
    return api.put(`/admin/users/${userId}`, userData);
  }

  async deactivateUser(userId) {
    return api.delete(`/admin/users/${userId}`);
  }

  async assignRole(userId, roleData) {
    return api.post(`/admin/users/${userId}/assign-role`, roleData);
  }

  async getAllFacilitiesAdmin() {
    return api.get('/admin/facilities');
  }

  async createFacility(facilityData) {
    return api.post('/admin/facilities', facilityData);
  }

  async updateFacility(facilityId, facilityData) {
    return api.put(`/admin/facilities/${facilityId}`, facilityData);
  }

  async assignFacilityManager(facilityId, managerUserId) {
    return api.post(`/admin/facilities/${facilityId}/assign-manager`, { manager_user_id: managerUserId });
  }

  async removeFacilityManager(facilityId, managerUserId) {
    return api.delete(`/admin/facilities/${facilityId}/managers/${managerUserId}`);
  }

  // Analytics endpoints - EXTENDED for new stages
  async getUserAnalytics() {
    return api.get('/admin/analytics/users');
  }

  async getScenarioAnalytics() {
    return api.get('/admin/analytics/scenarios');
  }

  async getFacilityAnalytics() {
    return api.get('/admin/analytics/facilities');
  }

  // NEW Analytics endpoint for extended LCA data
  async getExtendedLCAAnalytics() {
    return api.get('/admin/analytics/extended-lca');
  }

  // Health check
  async healthCheck() {
    return api.get('/health');
  }

  // Generic methods for dynamic API calls
  async get(endpoint, params = {}) {
    return api.get(endpoint, { params });
  }

  async post(endpoint, data = {}, config = {}) {
    return api.post(endpoint, data, config);
  }

  async put(endpoint, data = {}) {
    return api.put(endpoint, data);
  }

  async delete(endpoint) {
    return api.delete(endpoint);
  }

  // UTILITY METHODS for extended LCA data processing

  /**
   * Calculate stage group totals from emissions data
   */
  calculateStageGroups(emissions) {
    if (!emissions) return null;

    return {
      product_stages: (emissions.total_a1 || 0) + (emissions.total_a2 || 0) + (emissions.total_a3 || 0),
      construction_stage: emissions.total_a4_a5 || 0,
      use_stage: emissions.total_b2_b5 || 0,
      end_of_life_stage: emissions.total_c1_c4 || 0
    };
  }

  /**
   * Get stage percentages for visualization
   */
  calculateStagePercentages(emissions) {
    if (!emissions || !emissions.total_emissions) return null;

    const total = emissions.total_emissions;
    
    return {
      a1_percentage: ((emissions.total_a1 || 0) / total) * 100,
      a2_percentage: ((emissions.total_a2 || 0) / total) * 100,
      a3_percentage: ((emissions.total_a3 || 0) / total) * 100,
      a4_a5_percentage: ((emissions.total_a4_a5 || 0) / total) * 100,
      b2_b5_percentage: ((emissions.total_b2_b5 || 0) / total) * 100,
      c1_c4_percentage: ((emissions.total_c1_c4 || 0) / total) * 100
    };
  }

  /**
   * Format emissions value for display
   */
  formatEmissions(value, decimals = 2) {
    if (value === null || value === undefined) return '0.00';
    return Number(value).toFixed(decimals);
  }

  /**
   * Get emissions color based on value and thresholds
   */
  getEmissionsColor(value, thresholds = { low: 50, medium: 500, high: 1500 }) {
    if (value < thresholds.low) return '#26D7AE'; // Green
    if (value < thresholds.medium) return '#FFA726'; // Orange  
    if (value < thresholds.high) return '#FF7043'; // Red-Orange
    return '#EF5350'; // Red
  }

  /**
   * Validate scenario data before submission
   */
  validateScenarioData(scenarioData) {
    const errors = [];

    // Basic validation
    if (!scenarioData.name || scenarioData.name.trim() === '') {
      errors.push('Scenario name is required');
    }

    if (!scenarioData.facility || scenarioData.facility.trim() === '') {
      errors.push('Facility selection is required');
    }

    // Stage validation
    const requiredStages = ['quarry', 'transport', 'asphalt_manufacturing', 'transport_from_plant', 'maintenance', 'end_of_life'];
    
    requiredStages.forEach(stage => {
      if (!scenarioData[stage]) {
        console.warn(`Missing stage data for ${stage}, will use defaults`);
      }
    });

    // Production target validation (required for calculations)
    if (scenarioData.asphalt_manufacturing && !scenarioData.asphalt_manufacturing.production_target) {
      errors.push('Production target is required in manufacturing stage');
    }

    return {
      valid: errors.length === 0,
      errors: errors
    };
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;
